<template>
    <li class="answer-wrapper" :class="[props.role]">
        <div class="answer" v-html="formattedMessage" />
        <div v-if="props.role !== 'user' && !props.isReplying" class="feedback">
            <primevue-button v-tooltip.bottom="'Good response'" text class="feedback-button" :class="{ 'good-feedback': goodFeedback }" @click="giveFeedback(true)">
                <font-awesome-icon :icon="['fas', 'thumbs-up']" />
            </primevue-button>
            <primevue-button v-tooltip.bottom="'Bad response'" text class="feedback-button" :class="{ 'bad-feedback': badFeedback }" @click="giveFeedback(false)">
                <font-awesome-icon :icon="['fas', 'thumbs-down']" />
            </primevue-button>
            <primevue-button v-tooltip.bottom="'Give detailed feedback'" text class="feedback-button" @click="emit('openFeedbackInput', { question: props.lastQuestion, response: formattedMessage })">
                <font-awesome-icon :icon="['fas', 'comment-dots']" />
            </primevue-button>
        </div>
    </li>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import * as showdown from 'showdown';
import PrimevueButton from 'primevue/button';
import { IFeedback } from '@/interfaces/deltaGpt/IFeedback.ts';

const props = defineProps<{ message: string; role: string; isReplying: boolean; lastQuestion: string }>();

const emit = defineEmits<{ giveFeedback: [IFeedback]; openFeedbackInput: [{ question: string; response: string }]}>();

const goodFeedback = ref<boolean>(false);
const badFeedback = ref<boolean>(false);

function giveFeedback (isGood: boolean): void {
    if (isGood) {
        goodFeedback.value = true;
        badFeedback.value = false;
    } else {
        goodFeedback.value = false;
        badFeedback.value = true;
    }

    const feedback: IFeedback = {
        question: props.lastQuestion,
        response: props.message,
        goodResponse: isGood,
    };

    emit('giveFeedback', feedback);
}

const formattedMessage = computed(() => {
    if (!props.message) return '';

    const message = props.message.normalize('NFC').split('[pageIds]')[0];

    const singleSentenceReferences = message.match(/\{(\d+):(\d+)\}/g);
    const multiSentenceReferences = message.match(/\{(\d+):(\d+)-(\d+)\}/g);

    let result = message;
    singleSentenceReferences?.forEach((reference) => {
        const [page, start] = reference.slice(1, -1).split(':');
        const aTag = document.createElement('button');
        aTag.dataset.reference = `${page}:${start}-${start}`;
        aTag.innerText = '[source]';

        result = result.replace(reference, aTag.outerHTML);
    });

    multiSentenceReferences?.forEach((reference) => {
        const [page, sentences] = reference.slice(1, -1).split(':');
        const [start, end] = sentences.split('-');
        const aTag = document.createElement('button');
        aTag.dataset.reference = `${page}:${start}-${end}`;
        aTag.innerText = '[source]';

        result = result.replace(reference, aTag.outerHTML);
    });

    // check if format is like this instead {7:1-7:38}
    const singleSentenceReferences2 = result.match(/\{(\d+):(\d+)-(\d+):(\d+)\}/g);
    singleSentenceReferences2?.forEach((reference) => {
        const pages = reference.slice(1, -1).split('-');
        const page = pages[0].split(':')[0];
        const start = pages[0].split(':')[1];
        const end = pages[1].split(':')[1];

        const aTag = document.createElement('button');
        aTag.dataset.reference = `${page}:${start}-${end}`;
        aTag.innerText = '[source]';

        result = result.replace(reference, aTag.outerHTML);
    });

    // Check if format includes file name like this {ztyie9_2:1-2} and extract file name
    const singleSentenceReferences3 = result.match(/\{(\w+):(\d+)-(\d+)\}/g);
    singleSentenceReferences3?.forEach((reference) => {
        const pages = reference.slice(1, -1).split(':');
        const page = pages[0].split('-')[0];
        const start = pages[1].split('-')[0];
        const end = pages[1].split('-')[1];

        const aTag = document.createElement('button');
        aTag.dataset.reference = `${page}:${start}-${end}`;
        aTag.innerText = '[source]';

        result = result.replace(reference, aTag.outerHTML);
    });

    // check if format is like this instead {ztyie9_7:1-7:38}
    const singleSentenceReferences4 = result.match(/\{(\w+):(\d+)-(\d+):(\d+)\}/g);
    singleSentenceReferences4?.forEach((reference) => {
        const pages = reference.slice(1, -1).split(':');
        const page = pages[0].split('-')[0];
        const start = pages[1].split('-')[0];
        const end = pages[1].split('-')[1];

        const aTag = document.createElement('button');
        aTag.dataset.reference = `${page}:${start}-${end}`;
        aTag.innerText = '[source]';

        result = result.replace(reference, aTag.outerHTML);
    });

    // check if format is like this instead {ztyie9_7:1}
    const singleSentenceReferences5 = result.match(/\{(\w+):(\d+)\}/g);
    singleSentenceReferences5?.forEach((reference) => {
        const pages = reference.slice(1, -1).split(':');
        const page = pages[0];
        const start = pages[1];

        const aTag = document.createElement('button');
        aTag.dataset.reference = `${page}:${start}-${start}`;
        aTag.innerText = '[source]';

        result = result.replace(reference, aTag.outerHTML);
    });

    return convertToHTML(result);
});

function convertToHTML (data: string): string {
    const converter = new showdown.Converter({
        noHeaderId: true,
        simplifiedAutoLink: true,
        excludeTrailingPunctuationFromURLs: true,
        literalMidWordUnderscores: true,
        simpleLineBreaks: true,
        openLinksInNewWindow: true,
        backslashEscapesHTMLTags: true,
    });
    return converter.makeHtml(data);
}

</script>
<style lang="scss" scoped>
.answer :deep(button) {
    text-decoration: underline;
    color: var(--actionColour);
    font-weight: bold;
    background: none;
    border: 0;
    margin-left: 0.5ch;
    margin-right: 0.5ch;
    font-size: var(--extraSmallFontSize);
    cursor: pointer;
}

:deep(.answer) {
    position: relative;
    line-height: 1.5;
    font-size: var(--smallFontSize);

    p,
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    ul,
    ol,
    li,
    table,
    blockquote,
    pre,
    code {
        &+* {
            margin-top: var(--largeSpacing);
        }
    }

    p {
        margin: var(--defaultSpacing) 0;
    }

    p > button {
        margin-left: 0;
        margin-right: 0;
        padding-right: 0;

    }

    strong {
        font-weight: 700;
    }

    em {
        font-style: italic;
    }

    a {
        color: var(--lcpGrapefruit);
        text-decoration: underline;

        &:hover {
            text-decoration: none;
        }
    }

    ul {
        list-style: square;
        color: var(--lcpBlue);
        list-style-position: inside;
        margin-left: 0;

        li {
            padding: 0.5rem;
            color: var(--primaryFont);
        }
    }

    ol {
        padding-inline-start: 20px;
    }

    table {
        margin: var(--largeSpacing) 0;
        border-radius: 0.5rem;

        thead {
            color: var(--primaryFont);
            border-radius: 0.5rem;
        }

        td,
        th {
            padding: var(--smallSpacing);
            font-size: var(--extraSmallFontSize);
            &:not(:last-child) {
                border-right: 1px solid #ccc;
            }
        }

        tbody:nth-child(odd) {
            background: #f4f4f4;
        }
    }

    h1 {
        font-weight: 700;
        font-size: var(--largeFontSize);
        margin: var(--largeSpacing) var(--defaultSpacing);
    }

    h2 {
        font-weight: 700;
        font-size: var(--mediumFontSize);
        margin: var(--largeSpacing) var(--defaultSpacing);
    }

    h3 {
        font-size: var(--mediumFontSize);
        margin: var(--largeSpacing) var(--defaultSpacing);
        font-weight: 700;
    }

    h4 {
        font-weight: 700;
        font-size: var(--mediumFontSize);
        margin: var(--largeSpacing) var(--defaultSpacing);
    }
}

.answer-wrapper {
    position: relative;
    text-align: left;
    padding: 0 var(--defaultSpacing);
    background: var(--neutral300);
    backdrop-filter: blur(5px);
    border: 1px solid var(--neutral400);
    border-radius: 2rem 2rem 2rem 0;
    box-shadow: rgba(0, 0, 0, 0.05) 2px 2px 20px;
    align-self: flex-start;
    color: var(--primaryFont);
    // overflow: hidden;

    &.user {
        align-self: flex-end;
        border-radius: 2rem 2rem 0 2rem;
        background: var(--neutral200);
        backdrop-filter: blur(5px);
        border: 1px solid var(--neutral400);
        font-size: var(--extraSmallFontSize);
        color: var(--primaryFont);
        display: flex;
        gap: var(--smallSpacing);
        align-items: center;

        p {
            margin: 0 var(--defaultSpacing);
            margin-block-end: 0;
        }
    }

    &:not(.user) {
        padding: var(--defaultSpacing) 0;

        :deep(p) {
            margin: 0 var(--defaultSpacing);
        }
    }

    .feedback {
        position: absolute;
        background-color: var(--neutral300);
        border-radius: var(--borderRadiusOuter);
        border: 1px solid var(--neutral400);
        bottom: -25px;
        left: 20px;
        display: flex;
        gap: var(--smallSpacing);
        visibility: hidden;

        .feedback-button {
            color: var(--primaryFont);

            &:hover {
                background: transparent;
            }
        }

        .good-feedback {
            color: var(--successGreen);
        }

        .bad-feedback {
            color: var(--dangerRed);
        }
    }

    &:hover {
        .feedback {
            visibility: visible;
        }
    }
}
</style>
